/* cspell:disable */
import { useMsal } from '@azure/msal-react';
import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { AuthState, UserInfo, msalAccountToUserInfo } from './types';
import { loginRequest } from './msalConfig';
import {
  acquireTokenWithFallback,
  getCurrentTokenExpiration,
  getCurrentAccount,
  attemptSilentTokenRefresh,
} from './utils/tokenManagement';
import { TOKEN_VALIDITY_SECONDS } from './constants';

/**
 * Custom hook to manage active account and user info
 *
 * @param instance - The MSAL instance
 * @param accounts - Array of available accounts
 * @returns Object containing active account, user info, and refresh function
 */
function useActiveAccount(
  instance: IPublicClientApplication,
  accounts: AccountInfo[],
): {
  activeAccount: AccountInfo | null;
  user: UserInfo | undefined;
  refreshUserInfo: () => void;
  tokenRefreshTrigger: number;
} {
  const [user, setUser] = useState<UserInfo | undefined>(undefined);
  // Trigger to force recalculation of memoized values after token refresh
  const [tokenRefreshTrigger, setTokenRefreshTrigger] = useState(0);

  const activeAccount = useMemo(() => {
    return instance.getActiveAccount() ?? accounts[0] ?? null;
    // The trigger is intentionally unused inside the callback - it forces memo invalidation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts, tokenRefreshTrigger]);

  const userInfo = useMemo(() => {
    if (activeAccount) {
      const extractedUserInfo = msalAccountToUserInfo(activeAccount);
      return extractedUserInfo;
    }
    return undefined;
  }, [activeAccount]);

  useEffect(() => {
    setUser(userInfo);
  }, [userInfo]);

  const refreshUserInfo = useCallback(() => {
    // Fetch FRESH account from MSAL instance instead of using stale closure
    // This ensures we get the updated exp claim after token refresh
    const freshAccount = instance.getActiveAccount();
    setUser(freshAccount ? msalAccountToUserInfo(freshAccount) : undefined);
    // Increment trigger to force tokenExpiresAt recalculation
    setTokenRefreshTrigger((prev) => prev + 1);
  }, [instance]);

  return { activeAccount, user, refreshUserInfo, tokenRefreshTrigger };
}

/**
 * Simplified authentication actions hook aligned with MSAL React patterns
 *
 * @param instance - The MSAL instance
 * @param accounts - Array of available accounts
 * @param refreshUserInfo - Function to refresh user information
 * @returns Object containing authentication action functions
 */
function useSimplifiedEntraAuthActions(
  instance: IPublicClientApplication,
  accounts: AccountInfo[],
  refreshUserInfo: () => void,
): {
  initialize: () => Promise<void>;
  login: () => Promise<void>;
  loginWithRedirect: () => Promise<void>;
  logout: () => Promise<void>;
  updateToken: (forceRefresh?: boolean) => Promise<boolean>;
  getAccessToken: () => Promise<string | undefined>;
} {
  const login = useCallback(async (): Promise<void> => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const loginWithRedirect = useCallback(async (): Promise<void> => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const logout = useCallback(async (): Promise<void> => {
    const currentAccount = instance.getActiveAccount() ?? accounts[0];
    const logoutRequest = {
      account: currentAccount,
      postLogoutRedirectUri: globalThis.location.origin,
    };
    await instance.logoutPopup(logoutRequest);
  }, [instance, accounts]);

  const updateToken = useCallback(
    async (forceRefresh?: boolean): Promise<boolean> => {
      const currentAccount = getCurrentAccount(instance, accounts);
      if (!currentAccount) return false;

      return await attemptSilentTokenRefresh(
        instance,
        currentAccount,
        refreshUserInfo,
        forceRefresh,
      );
    },
    [instance, accounts, refreshUserInfo],
  );

  const getAccessToken = useCallback(async (): Promise<string | undefined> => {
    const currentAccount = getCurrentAccount(instance, accounts);
    if (!currentAccount) return undefined;

    const tokenResult = await acquireTokenWithFallback(
      instance,
      currentAccount,
    );
    return tokenResult?.accessToken;
  }, [instance, accounts]);

  const initialize = useCallback(async (): Promise<void> => {
    // MSAL React handles initialization automatically
    // This is a no-op for compatibility with AuthState interface
  }, []);

  return useMemo(
    () => ({
      initialize,
      login,
      loginWithRedirect,
      logout,
      updateToken,
      getAccessToken,
    }),
    [initialize, login, loginWithRedirect, logout, updateToken, getAccessToken],
  );
}

/**
 * Simplified Entra ID authentication hook aligned with MSAL React patterns
 * Relies on MSAL React's built-in initialization and state management
 *
 * This hook provides a unified authentication interface for Microsoft Entra ID
 * that integrates seamlessly with MSAL React's built-in state management.
 *
 * @returns AuthState object containing authentication status and actions
 */
export function useEntraAuth(): AuthState {
  const { instance, accounts, inProgress } = useMsal();
  const [tokenMinValiditySeconds] = useState(
    process.env.NODE_ENV === 'development'
      ? TOKEN_VALIDITY_SECONDS.DEVELOPMENT
      : TOKEN_VALIDITY_SECONDS.PRODUCTION,
  );

  const { activeAccount, user, refreshUserInfo, tokenRefreshTrigger } =
    useActiveAccount(instance, accounts);

  const actions = useSimplifiedEntraAuthActions(
    instance,
    accounts,
    refreshUserInfo,
  );

  // Get current token expiration for lifecycle management
  // Include tokenRefreshTrigger to force recalculation after token refresh
  // The trigger is intentionally unused inside the callback - it forces memo invalidation
  const tokenExpiresAt = useMemo(() => {
    return getCurrentTokenExpiration(instance, accounts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts, tokenRefreshTrigger]);

  // MSAL React handles initialization - we're only initialized when not in startup/redirect
  const initialized =
    inProgress !== 'startup' && inProgress !== 'handleRedirect';

  return useMemo(
    () => ({
      initialized,
      authenticated: !!activeAccount && (accounts?.length ?? 0) > 0,
      user,
      tokenMinValiditySeconds,
      tokenExpiresAt,
      actions,
    }),
    [
      initialized,
      activeAccount,
      accounts?.length,
      user,
      tokenMinValiditySeconds,
      tokenExpiresAt,
      actions,
    ],
  );
}
